// src/finance/engine/defaults.ts
// Défauts fiscaux vérifiés le 2026-08-20 :
// - Barème IR 2026 (revenus 2025, LF 2026) — service-public.gouv.fr
// - Abattement 10 % plafonné à 14 555 € (revenus 2025) — impots.gouv.fr
// - PFU 31,4 % = 12,8 % IR + 18,6 % PS (CSG +1,4 pt, LFSS 2026)
// - UK CT 2026/27 : 19 %/25 %, £50k/£250k (≈ 58 500 €/292 500 € à 1,17 €/£), relief 3/200 — gov.uk
// - Cotisations firmes étrangères (indicatif) : patronal 40 %, salarial 22 % — urssaf.eu
import type { FinanceSettings } from './types'

export const DEFAULT_FINANCE_SETTINGS: FinanceSettings = {
  direction: 'netToRevenue',
  targetAmount: 4000,
  billableDays: 15,
  hoursPerDay: 7,
  mixSalaryShare: 0.5,
  appRevenueGross: 0,
  // 30 % : le taux standard hors UE. Les ventes UE passent à 26 % au 1er octobre
  // 2026 (règlement DMA), et 15 % s'appliquent au Small Business Program comme
  // aux abonnés de plus d'un an — le taux moyen réel baisse donc avec le temps.
  appCommissionRate: 0.3,
  payerMode: 'mix',
  payerRegime: 'uk',
  tax: {
    ctSmallRate: 0.19,
    ctMainRate: 0.25,
    ctLowerLimit: 58_500,
    ctUpperLimit: 292_500,
    // Déprécié (C4) : le moteur dérive la fraction des quatre paramètres ci-dessus.
    // Valeur conservée à titre informatif (3/200 = la fraction UK officielle).
    ctMarginalReliefFraction: 3 / 200,
    // IS français (comparatif) : 15 % ≤ 42 500 € / 25 % au-delà — LF 2026 sans
    // changement (vérifié le 2026-08-20 : service-public.fr, BOFiP BOI-IS-LIQ-20).
    frIsReducedRate: 0.15,
    frIsReducedCap: 42_500,
    frIsMainRate: 0.25,
    pfuRate: 0.314,
    employerRate: 0.4,
    employeeRate: 0.22,
    irBrackets: [
      { upTo: 11_600, rate: 0 },
      { upTo: 29_579, rate: 0.11 },
      { upTo: 84_577, rate: 0.3 },
      { upTo: 181_917, rate: 0.41 },
      { upTo: null, rate: 0.45 },
    ],
    fiscalParts: 1,
    salaryAllowanceRate: 0.1,
    salaryAllowanceCap: 14_555,
    // CSG non déductible 2,40 % + CRDS 0,50 % = 2,90 % d'une assiette valant
    // 98,25 % du brut (abattement forfaitaire 1,75 % pour frais pro, dans la
    // limite de 4 PASS) → 0,029 × 0,9825 ≈ 2,85 % du brut réintégrés au
    // revenu imposable (service-public.gouv.fr F2971, vérifié le 2026-08-24).
    nonDeductibleRate: 0.0285,
    // Décote 2026 (revenus 2025) — art. 197, 4-I-a CGI, LF 2026 (+0,9 %) :
    // seuils 1 982 €/3 277 €, bases 897 €/1 483 €, taux 45,25 %
    // (BOFiP BOI-IR-LIQ-20-20-30 du 07/04/2026).
    decoteThresholdSingle: 1_982,
    decoteThresholdCouple: 3_277,
    decoteBaseSingle: 897,
    decoteBaseCouple: 1_483,
    decoteRate: 0.4525,
    // Plafonnement du quotient familial : 1 807 € par demi-part supplémentaire
    // (droit commun, revenus 2025) — BOFiP BOI-IR-LIQ-20-20-20 du 07/04/2026.
    halfPartCap: 1_807,
  },
}
